/** Filter bar for the report pages (P53) — instrument, day, India VIX
 * (field + min/max) and entry-time ranges, mirroring the operator's
 * reference dashboard. Instrument/date apply on click; the VIX and
 * time-range groups are DRAFTS that only take effect on their Apply
 * button (typing in a bound doesn't instantly filter everything out). */

import { useEffect, useState, type ReactElement } from "react";
import type { TradeComparison, VixDay } from "@/lib/reportsApi";
import { istDayOf } from "./exitStats";

export type InstrumentFilter = "ALL" | "NIFTY" | "SENSEX";

export type VixField = "close" | "open" | "high" | "low";

export interface TimeRange {
  from: string; // HH:MM (IST)
  to: string; // HH:MM (IST)
}

export interface ReportFilterState {
  instrument: InstrumentFilter;
  /** "all" or a YYYY-MM-DD day present in the data. */
  day: string;
  /** Which VIX value the min/max bounds compare against. */
  vixField: VixField;
  vixMin: string;
  vixMax: string;
  /** Entry-time windows (IST); a trade matches if it falls in ANY. */
  timeRanges: TimeRange[];
}

export const EMPTY_FILTERS: ReportFilterState = {
  instrument: "ALL",
  day: "all",
  vixField: "close",
  vixMin: "",
  vixMax: "",
  timeRanges: [],
};

// ---------------------------------------------------------------------------
// Pure filtering (operates on the APPLIED filter state)
// ---------------------------------------------------------------------------
function instrumentOf(trade: TradeComparison): InstrumentFilter {
  const symbol = trade.contract_symbol ?? "";
  if (symbol.startsWith("NIFTY")) return "NIFTY";
  if (symbol.startsWith("SENSEX") || symbol.startsWith("BSX")) return "SENSEX";
  return "ALL";
}

export function applyFilters(
  trades: TradeComparison[],
  vix: VixDay[],
  filters: ReportFilterState,
): TradeComparison[] {
  const vixValueByDay = new Map(vix.map((v) => [v.trade_date, v[filters.vixField]]));
  const min = filters.vixMin === "" ? null : Number(filters.vixMin);
  const max = filters.vixMax === "" ? null : Number(filters.vixMax);
  return trades.filter(
    (t) =>
      matchesInstrument(t, filters) &&
      matchesDay(istDayOf(t.report_at), filters) &&
      matchesVix(vixValueByDay.get(istDayOf(t.report_at)), min, max) &&
      matchesTime(istTimeOf(t.entry_at), filters.timeRanges),
  );
}

function matchesInstrument(trade: TradeComparison, filters: ReportFilterState): boolean {
  return filters.instrument === "ALL" || instrumentOf(trade) === filters.instrument;
}

function matchesDay(day: string, filters: ReportFilterState): boolean {
  return filters.day === "all" || day === filters.day;
}

function matchesVix(value: number | undefined, min: number | null, max: number | null): boolean {
  if (min === null && max === null) return true;
  if (value === undefined) return false;
  if (min !== null && value < min) return false;
  return max === null || value <= max;
}

function istTimeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
}

function matchesTime(time: string, ranges: TimeRange[]): boolean {
  const active = ranges.filter((r) => r.from !== "" && r.to !== "");
  if (active.length === 0) return true;
  return active.some((r) => time >= r.from && time <= r.to);
}

// ---------------------------------------------------------------------------
// Bar
// ---------------------------------------------------------------------------
interface GroupProps {
  draft: ReportFilterState;
  setDraft: (updater: (prev: ReportFilterState) => ReportFilterState) => void;
  filters: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
}

export function ReportFilterBar({
  filters,
  days,
  onChange,
}: Readonly<{
  filters: ReportFilterState;
  days: string[];
  onChange: (next: ReportFilterState) => void;
}>): ReactElement {
  // Draft state for the Apply-gated groups; re-synced whenever the
  // applied filters change (Apply, Clear, month switch).
  const [draft, setDraft] = useState<ReportFilterState>(filters);
  useEffect(() => {
    setDraft(filters);
  }, [filters]);
  return (
    <div className="mb-4 flex flex-col gap-2 rounded-ctl border border-line bg-surface-1 px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <DaySelect filters={filters} days={days} onChange={onChange} />
        <InstrumentChips filters={filters} onChange={onChange} />
        <button
          type="button"
          className="btn btn-secondary ml-auto px-2 py-1 text-xs"
          onClick={() => {
            onChange(EMPTY_FILTERS);
          }}
        >
          <i className="ti ti-filter-off text-xs" /> Reset filters
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <VixGroup draft={draft} setDraft={setDraft} filters={filters} onChange={onChange} />
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <TimeRangeGroup draft={draft} setDraft={setDraft} filters={filters} onChange={onChange} />
      </div>
    </div>
  );
}

function InstrumentChips({
  filters,
  onChange,
}: Readonly<{
  filters: ReportFilterState;
  onChange: (next: ReportFilterState) => void;
}>): ReactElement {
  const options: InstrumentFilter[] = ["ALL", "NIFTY", "SENSEX"];
  return (
    <span className="flex items-center gap-1.5">
      <span className="font-medium uppercase tracking-wider text-ink-secondary">Instrument</span>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={`rounded-ctl border px-2 py-1 font-medium ${
            filters.instrument === o
              ? "border-accent-border bg-accent-bg text-accent"
              : "border-line bg-surface-2 text-ink-secondary hover:bg-surface-1"
          }`}
          onClick={() => {
            onChange({ ...filters, instrument: o });
          }}
        >
          {o === "ALL" ? "All" : o}
        </button>
      ))}
    </span>
  );
}

function DaySelect({
  filters,
  days,
  onChange,
}: Readonly<{
  filters: ReportFilterState;
  days: string[];
  onChange: (next: ReportFilterState) => void;
}>): ReactElement {
  return (
    <label className="flex items-center gap-1.5">
      <span className="font-medium uppercase tracking-wider text-ink-secondary">Date</span>
      <select
        className="rounded border border-line bg-surface-2 px-1.5 py-1 text-xs"
        value={filters.day}
        onChange={(e) => {
          onChange({ ...filters, day: e.target.value });
        }}
      >
        <option value="all">All dates</option>
        {days.map((d) => (
          <option key={d} value={d}>
            {d}
          </option>
        ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// VIX group (draft + Apply)
// ---------------------------------------------------------------------------
function VixGroup({ draft, setDraft, filters, onChange }: Readonly<GroupProps>): ReactElement {
  return (
    <span className="flex items-center gap-1.5">
      <span className="font-medium uppercase tracking-wider text-ink-secondary">VIX</span>
      <VixInputs draft={draft} setDraft={setDraft} />
      <button
        type="button"
        className="btn btn-primary px-2 py-1 text-xs"
        onClick={() => {
          onChange({
            ...filters,
            vixField: draft.vixField,
            vixMin: draft.vixMin,
            vixMax: draft.vixMax,
          });
        }}
      >
        <i className="ti ti-player-play text-xs" /> Apply
      </button>
      <button
        type="button"
        className="btn btn-secondary px-2 py-1 text-xs"
        onClick={() => {
          onChange({ ...filters, vixField: "close", vixMin: "", vixMax: "" });
        }}
      >
        <i className="ti ti-x text-xs" /> Clear
      </button>
    </span>
  );
}

function VixInputs({
  draft,
  setDraft,
}: Readonly<Pick<GroupProps, "draft" | "setDraft">>): ReactElement {
  return (
    <>
      <select
        aria-label="VIX field"
        className="rounded border border-line bg-surface-2 px-1.5 py-1 text-xs"
        value={draft.vixField}
        onChange={(e) => {
          setDraft((prev) => ({ ...prev, vixField: e.target.value as VixField }));
        }}
      >
        <option value="close">Close</option>
        <option value="open">Open</option>
        <option value="high">High</option>
        <option value="low">Low</option>
      </select>
      <input
        type="number"
        placeholder="min"
        aria-label="VIX minimum"
        className="w-16 rounded border border-line bg-surface-2 px-1.5 py-1 text-xs"
        value={draft.vixMin}
        onChange={(e) => {
          setDraft((prev) => ({ ...prev, vixMin: e.target.value }));
        }}
      />
      <input
        type="number"
        placeholder="max"
        aria-label="VIX maximum"
        className="w-16 rounded border border-line bg-surface-2 px-1.5 py-1 text-xs"
        value={draft.vixMax}
        onChange={(e) => {
          setDraft((prev) => ({ ...prev, vixMax: e.target.value }));
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Time-range group (draft rows + Apply / Clear All)
// ---------------------------------------------------------------------------
function TimeRangeGroup({
  draft,
  setDraft,
  filters,
  onChange,
}: Readonly<GroupProps>): ReactElement {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="font-medium uppercase tracking-wider text-ink-secondary">Time ranges</span>
      {draft.timeRanges.map((range, i) => (
        <RangeRow key={rangeKey(i)} index={i} range={range} setDraft={setDraft} />
      ))}
      <button
        type="button"
        className="rounded-ctl border border-line bg-surface-2 px-2 py-1 font-medium text-ink-secondary hover:bg-surface-1"
        onClick={() => {
          setDraft((prev) => ({
            ...prev,
            timeRanges: [...prev.timeRanges, { from: "", to: "" }],
          }));
        }}
      >
        + Add Range
      </button>
      {/* Apply/Clear appear only once a range exists — otherwise they are
          noise next to the global Reset (operator feedback). */}
      {draft.timeRanges.length > 0 && (
        <TimeRangeActions draft={draft} filters={filters} onChange={onChange} />
      )}
    </span>
  );
}

function TimeRangeActions({
  draft,
  filters,
  onChange,
}: Readonly<Omit<GroupProps, "setDraft">>): ReactElement {
  return (
    <>
      <button
        type="button"
        className="btn btn-primary px-2 py-1 text-xs"
        onClick={() => {
          onChange({ ...filters, timeRanges: draft.timeRanges });
        }}
      >
        <i className="ti ti-player-play text-xs" /> Apply
      </button>
      <button
        type="button"
        className="btn btn-secondary px-2 py-1 text-xs"
        onClick={() => {
          onChange({ ...filters, timeRanges: [] });
        }}
      >
        <i className="ti ti-x text-xs" /> Clear All
      </button>
    </>
  );
}

/** Rows are positional; index-derived keys are fine for this small list. */
function rangeKey(index: number): string {
  return `range-${String(index)}`;
}

function RangeRow({
  index,
  range,
  setDraft,
}: Readonly<{
  index: number;
  range: TimeRange;
  setDraft: GroupProps["setDraft"];
}>): ReactElement {
  const patch = (part: Partial<TimeRange>): void => {
    setDraft((prev) => ({
      ...prev,
      timeRanges: prev.timeRanges.map((r, i) => (i === index ? { ...r, ...part } : r)),
    }));
  };
  const remove = (): void => {
    setDraft((prev) => ({
      ...prev,
      timeRanges: prev.timeRanges.filter((_, i) => i !== index),
    }));
  };
  return (
    <span className="flex items-center gap-1">
      <TimeInput
        label={`Time range ${String(index + 1)} from`}
        value={range.from}
        onValue={(v) => {
          patch({ from: v });
        }}
      />
      <span className="text-ink-muted">to</span>
      <TimeInput
        label={`Time range ${String(index + 1)} to`}
        value={range.to}
        onValue={(v) => {
          patch({ to: v });
        }}
      />
      <button
        type="button"
        aria-label={`Remove time range ${String(index + 1)}`}
        className="text-ink-muted hover:text-danger"
        onClick={remove}
      >
        <i className="ti ti-x text-xs" />
      </button>
    </span>
  );
}

function TimeInput({
  label,
  value,
  onValue,
}: Readonly<{ label: string; value: string; onValue: (v: string) => void }>): ReactElement {
  return (
    <input
      type="time"
      aria-label={label}
      className="rounded border border-line bg-surface-2 px-1.5 py-1 text-xs"
      value={value}
      onChange={(e) => {
        onValue(e.target.value);
      }}
    />
  );
}
